/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import classnames from 'classnames';
import { COMMENT_VIEWING_STATUS } from 'features/comments/constants';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import { useEventHub, CommentEventType } from '@pwc/event-hub-ui';
import { useShowInlineComments } from 'common/hooks/useShowInlineComments';
import {
  getComments,
  getViewingStatus,
  setCommentPanelOpened,
  viewUnresolvedComments,
  setRibbonPanelItem,
  getRibbonPanelItem,
} from '../../../features/comments/commentsSlice';
import EnableForProjectState from '../../../features/security/EnableForProjectState';
import featureFlags from '../../helpers/featureFlags';
import CommentEditor from '../../plugins/CommentEditor';
import { RibbonButtonTypeProps } from '../CommentPanel/RibbonPanelButton';
import RibbonWrapper from '../CommentPanel/RibbonWrapper';
import RibbonPanel from './RibbonPanel';
import { useShouldDisplayReportPreview } from './useShouldDisplayReportPreview';
import { setReportPreviewProps } from '../../../features/analyses/libraryItemsSlice';
import { useShouldDisplayVersioningIcon } from './useShouldDisplayVersioningIcon';

type Props = {
  projectId: number;
};

// TODO typed EnableForProjectState
type EnableForProjectStateProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  isProjectDisabled: boolean;
};

const RibbonPanelContainer = ({ projectId }: Props): JSX.Element | null => {
  const {
    entityId,
    entityType,
    entityVersion,
    versionRequired,
    isEntitySharedWithClient,
    showCommentsPanel,
    commentPanelOpened, // TODO typed selectors: don't use it,
    embedEditor = true,
  } = useSelector(getComments);

  const { showComments } = useShowInlineComments();

  const mounted = useRef(false);

  const displayReportPreview = useShouldDisplayReportPreview();
  const displayVersioning = useShouldDisplayVersioningIcon();
  const versionReadyOrNotNeeded = !(versionRequired && entityVersion === -1);
  const guardEntityVersion = versionRequired ? entityVersion : -1;
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const { eventHub } = useEventHub();

  const dispatch = useDispatch();
  const viewingStatus = useSelector(getViewingStatus) as
    | 'unresolved'
    | 'resolved';
  const ribbonPanelItemType = useSelector(getRibbonPanelItem);

  const isRibbonPanelOpened = useMemo(
    () => !!ribbonPanelItemType?.type,
    [ribbonPanelItemType]
  );

  useEffect(() => {
    eventHub?.publish(
      '@ribbon-panel-opened' as CommentEventType,
      isRibbonPanelOpened
    );
  }, [eventHub, isRibbonPanelOpened]);

  const handlePanelButtonClick = ({ type }: RibbonButtonTypeProps) => {
    dispatch(setRibbonPanelItem({ type }));

    if (type === 'comment') {
      dispatch(
        setReportPreviewProps({
          isDisplayed: false,
        })
      );

      if (viewingStatus !== COMMENT_VIEWING_STATUS.UNRESOLVED) {
        dispatch(viewUnresolvedComments());
      }

      showComments(true);
    } else if (type === 'preview') {
      // TODO: show report preview component
      dispatch(
        setReportPreviewProps({
          isDisplayed: true,
        })
      );
    } else if (type === 'versioning') {
      dispatch(
        setReportPreviewProps({
          isDisplayed: false,
        })
      );
    } else {
      showComments(false);
      eventHub?.publish('commenting-remove-freeze-area' as CommentEventType);
      eventHub?.publish('commenting-close-panel' as CommentEventType);
    }
  };

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }

    if (ribbonPanelItemType.type == null) {
      dispatch(setCommentPanelOpened(false));
    } else {
      dispatch(setCommentPanelOpened(true));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ribbonPanelItemType.type]);

  useEffect(() => {
    if (commentPanelOpened && ribbonPanelItemType.type == null) {
      handlePanelButtonClick({ type: 'comment' });
    }
    if (!commentPanelOpened && ribbonPanelItemType != null) {
      handlePanelButtonClick({ type: null });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentPanelOpened]);

  const handlePanelClose = () => {
    dispatch(
      setReportPreviewProps({
        isDisplayed: false,
      })
    );
    // On closing the comment panel without adding comment and if we open the comment panel it still has input-container-active
    eventHub?.publish('@comment-add-comment-thread' as CommentEventType, null);
    dispatch(setRibbonPanelItem({ type: null }));
  };

  const visibleRibbonOptions = useMemo(() => {
    if (displayReportPreview && displayVersioning)
      return ['comment', 'preview', 'versioning'];

    if (displayReportPreview && !displayVersioning)
      return ['comment', 'preview'];

    if (!displayReportPreview && displayVersioning)
      return ['comment', 'versioning'];

    return ['comment'];
  }, [displayReportPreview, displayVersioning]);

  const isPanelOpened = useMemo(
    () => ribbonPanelItemType.type != null,
    [ribbonPanelItemType.type]
  );

  if (showCommentsPanel && versionReadyOrNotNeeded) {
    const containerClassName = classnames(
      'comment-panel',
      isPwcUser ? 'current-user-is-pwc' : 'current-user-is-client',
      {
        'selection-comments-disabled': !featureFlags.selectionCommentsEnabled,
      }
    );

    return (
      <EnableForProjectState projectId={projectId}>
        {({ isProjectDisabled }: EnableForProjectStateProps) => (
          <div
            className={containerClassName}
            data-testid="comment-panel-container"
          >
            {commentPanelOpened &&
              embedEditor &&
              ribbonPanelItemType.type === 'comment' && (
                <div>
                  <span hidden data-testid="comment-editor">
                    <CommentEditor
                      entityType={entityType}
                      entityId={entityId as number}
                      entityVersion={guardEntityVersion}
                      isEntitySharedWithClient={
                        isEntitySharedWithClient as boolean
                      }
                      isProjectDisabled={isProjectDisabled}
                      loadComments
                      onSelectionChange={undefined}
                      formattingPlugin={undefined}
                    >
                      {undefined}
                    </CommentEditor>
                  </span>
                </div>
              )}
            <div
              className={
                isPanelOpened
                  ? 'wrapper-ribbon-panel-container ribbon-panel-mobile-view'
                  : 'wrapper-ribbon-panel-container'
              }
            >
              <RibbonWrapper
                ribbonType={ribbonPanelItemType.type}
                visibleButtons={visibleRibbonOptions}
                isEntitySharedWithClient={isEntitySharedWithClient}
                viewingStatus={viewingStatus}
                handlePanelButtonClick={(type) => handlePanelButtonClick(type)}
                isPanelOpened={isPanelOpened}
              />
              <RibbonPanel
                ribbonItem={ribbonPanelItemType.type}
                projectId={projectId}
                onClose={handlePanelClose}
              />
            </div>
          </div>
        )}
      </EnableForProjectState>
    );
  }
  return null;
};

export default RibbonPanelContainer;
